import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, ArrowRight } from "lucide-react";

interface Question {
  id: number;
  question: string;
  options: { value: string; label: string; weight: { [key: string]: number } }[];
}

interface QuestionnaireResults {
  cybersecurity: number;
  frontend: number;
  backend: number;
  programming: number;
}

const questions: Question[] = [
  {
    id: 1,
    question: "Ո՞ր ոլորտում ունեք նախկին փորձ կամ գիտելիքներ:",
    options: [
      { value: "none", label: "Փորձ չունեմ IT ոլորտում", weight: { cybersecurity: 1, frontend: 1, backend: 1, programming: 1 } },
      { value: "basic_computer", label: "Հիմնական համակարգչային գիտելիքներ", weight: { cybersecurity: 2, frontend: 2, backend: 2, programming: 2 } },
      { value: "web_basics", label: "Կայքերի հիմունքներ", weight: { frontend: 3, backend: 2, programming: 2, cybersecurity: 1 } },
      { value: "programming_basics", label: "Ծրագրավորման հիմունքներ", weight: { programming: 3, backend: 3, frontend: 2, cybersecurity: 2 } },
      { value: "security_interest", label: "Անվտանգության հետ կապված", weight: { cybersecurity: 3, backend: 2, programming: 2, frontend: 1 } }
    ]
  },
  {
    id: 2,
    question: "Ձեր հետաքրքությունն ավելի շատ ո՞ր ուղղությամբ է:",
    options: [
      { value: "visual_design", label: "Վիզուալ դիզայն և օգտատիրական ինտերֆեյս", weight: { frontend: 3, backend: 1, programming: 1, cybersecurity: 1 } },
      { value: "logic_algorithms", label: "Տրամաբանություն և ալգորիթմներ", weight: { programming: 3, backend: 3, frontend: 2, cybersecurity: 2 } },
      { value: "systems_security", label: "Համակարգերի պաշտպանություն", weight: { cybersecurity: 3, backend: 2, programming: 2, frontend: 1 } },
      { value: "data_processing", label: "Տվյալների մշակում և կազմակերպում", weight: { backend: 3, programming: 2, cybersecurity: 2, frontend: 1 } }
    ]
  },
  {
    id: 3,
    question: "Ինչպիսի՞ն է ձեր ուսուցման նպատակը:",
    options: [
      { value: "career_change", label: "Մասնագիտական վերաորակավորում", weight: { cybersecurity: 2, frontend: 2, backend: 2, programming: 2 } },
      { value: "skill_upgrade", label: "Առկա հմտությունների զարգացում", weight: { programming: 3, backend: 2, frontend: 2, cybersecurity: 2 } },
      { value: "first_profession", label: "Առաջին մասնագիտություն ստանալ", weight: { frontend: 3, programming: 2, backend: 2, cybersecurity: 1 } },
      { value: "specialization", label: "Մասնագիտական խորացում", weight: { cybersecurity: 3, backend: 3, programming: 2, frontend: 2 } }
    ]
  },
  {
    id: 4,
    question: "Որքա՞ն ժամանակ կարող եք հատկացնել ուսուցմանը:",
    options: [
      { value: "part_time", label: "2-3 ժամ օրական", weight: { frontend: 2, programming: 2, backend: 2, cybersecurity: 2 } },
      { value: "intensive", label: "4-6 ժամ օրական", weight: { cybersecurity: 3, backend: 3, programming: 3, frontend: 2 } },
      { value: "weekend", label: "Միայն շաբաթ-կիրակի", weight: { frontend: 3, programming: 2, backend: 1, cybersecurity: 1 } },
      { value: "flexible", label: "Ճկուն գրաֆիկ", weight: { frontend: 2, programming: 2, backend: 2, cybersecurity: 2 } }
    ]
  },
  {
    id: 5,
    question: "Ո՞ր ձևաչափն է ձեզ համար ավելի նախընտրելի:",
    options: [
      { value: "online", label: "Առցանց ուսուցում", weight: { frontend: 2, programming: 2, backend: 2, cybersecurity: 2 } },
      { value: "offline", label: "Ներկայացման ուսուցում", weight: { cybersecurity: 3, backend: 2, programming: 2, frontend: 2 } },
      { value: "hybrid", label: "Խառը ձևաչափ", weight: { frontend: 2, programming: 3, backend: 3, cybersecurity: 2 } },
      { value: "individual", label: "Անհատական դասեր", weight: { cybersecurity: 2, backend: 3, programming: 3, frontend: 2 } }
    ]
  },
  {
    id: 6,
    question: "Ինչպիսի՞ն է ձեր տեխնիկական բազան:",
    options: [
      { value: "beginner", label: "Նորեկ եմ տեխնոլոգիաներում", weight: { frontend: 3, programming: 2, backend: 1, cybersecurity: 1 } },
      { value: "intermediate", label: "Միջին մակարդակ", weight: { programming: 3, frontend: 2, backend: 2, cybersecurity: 2 } },
      { value: "advanced", label: "Բարձր մակարդակ", weight: { cybersecurity: 3, backend: 3, programming: 2, frontend: 2 } },
      { value: "expert", label: "Փորձագետ", weight: { cybersecurity: 3, backend: 3, programming: 3, frontend: 2 } }
    ]
  },
  {
    id: 7,
    question: "Ո՞ր աշխատանքային միջավայրն է ձեզ ավելի հետաքրքիր:",
    options: [
      { value: "creative", label: "Ստեղծագործական և վիզուալ", weight: { frontend: 3, programming: 1, backend: 1, cybersecurity: 1 } },
      { value: "analytical", label: "Վերլուծական և տրամաբանական", weight: { programming: 3, backend: 3, cybersecurity: 2, frontend: 1 } },
      { value: "security_focused", label: "Անվտանգություն և պաշտպանություն", weight: { cybersecurity: 3, backend: 2, programming: 2, frontend: 1 } },
      { value: "problem_solving", label: "Խնդիրների լուծում", weight: { programming: 3, backend: 3, cybersecurity: 2, frontend: 2 } }
    ]
  },
  {
    id: 8,
    question: "Ո՞ր ոլորտում կցանկանայիք աշխատել ապագայում:",
    options: [
      { value: "startups", label: "Նորարարական ստարտապներ", weight: { frontend: 3, programming: 2, backend: 2, cybersecurity: 1 } },
      { value: "corporations", label: "Մեծ կորպորացիաներ", weight: { cybersecurity: 3, backend: 3, programming: 2, frontend: 2 } },
      { value: "freelance", label: "Ազատ գործունեություն", weight: { frontend: 3, programming: 2, backend: 2, cybersecurity: 1 } },
      { value: "government", label: "Պետական կազմակերպություններ", weight: { cybersecurity: 3, backend: 2, programming: 2, frontend: 1 } }
    ]
  }
];

interface CareerQuestionnaireModeProps {
  onResults: (results: QuestionnaireResults) => void;
  onBack: () => void;
}

export const CareerQuestionnaireMode = ({ onResults, onBack }: CareerQuestionnaireModeProps) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [isCompleted, setIsCompleted] = useState(false);

  const handleAnswer = (value: string) => {
    setAnswers(prev => ({ ...prev, [questions[currentQuestion].id]: value }));
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      calculateResults();
    }
  };

  const calculateResults = () => {
    const results: QuestionnaireResults = {
      cybersecurity: 0,
      frontend: 0,
      backend: 0,
      programming: 0
    };

    Object.entries(answers).forEach(([questionId, answerValue]) => {
      const question = questions.find(q => q.id === parseInt(questionId));
      const selectedOption = question?.options.find(opt => opt.value === answerValue);
      
      if (selectedOption) {
        Object.entries(selectedOption.weight).forEach(([field, weight]) => {
          results[field as keyof QuestionnaireResults] += weight;
        });
      }
    });

    setIsCompleted(true);
    onResults(results);
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  if (isCompleted) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">Հարցաշարը ավարտված է!</h3>
        <p className="text-muted-foreground">Ձեր պատասխանները վերլուծվում են...</p>
      </div>
    );
  }

  const question = questions[currentQuestion];

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Հարց {currentQuestion + 1} / {questions.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="w-full" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{question.question}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {question.options.map((option) => (
            <Button
              key={option.value}
              variant="outline"
              className="w-full justify-start text-left h-auto py-3 px-4"
              onClick={() => handleAnswer(option.value)}
            >
              <span className="flex-1">{option.label}</span>
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          Հետ դառնալ
        </Button>
        {currentQuestion > 0 && (
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(prev => prev - 1)}
          >
            Նախորդ հարցը
          </Button>
        )}
      </div>
    </div>
  );
};